import { useState, useEffect } from 'react';
import Modal from '../ui/Modal';
import { SettingsTextField } from '../settings/settingsUi.jsx';
import { LEAD_STATUS_LABEL } from '../../utils/sitePublicStatusUi.js';
import { SOURCE_OPTIONS, buildManualSourcePath } from '../../utils/proLeadSource.js';

const EMPTY = {
  businessName: '',
  name: '',
  phone: '',
  email: '',
  city: '',
  utmSource: 'manuel',
  status: 'NEW',
  nextFollowUpAt: '',
  internalNotes: '',
};

export default function AddProspectModal({
  open,
  busy,
  onClose,
  onSubmit,
  title = 'Ajouter un prospect',
  submitLabel = 'Ajouter',
  initialBusinessName = '',
}) {
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setForm({
      ...EMPTY,
      businessName: initialBusinessName || '',
    });
    setError('');
  }, [open, initialBusinessName]);

  const setField = (field) => (value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleClose = () => {
    setForm(EMPTY);
    setError('');
    onClose?.();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.businessName.trim() || !form.name.trim()) {
      setError('Commerce et nom du contact sont obligatoires.');
      return;
    }
    setError('');
    const payload = {
      businessName: form.businessName.trim(),
      name: form.name.trim(),
      phone: form.phone.trim() || undefined,
      email: form.email.trim() || undefined,
      city: form.city.trim() || undefined,
      status: form.status,
      internalNotes: form.internalNotes.trim() || undefined,
      nextFollowUpAt: form.nextFollowUpAt
        ? new Date(form.nextFollowUpAt).toISOString()
        : undefined,
      sourcePath: buildManualSourcePath(form.utmSource || 'manuel'),
      utmSource: form.utmSource || 'manuel',
      message: form.internalNotes.trim() || 'Ajout manuel Super Admin',
    };
    try {
      await onSubmit(payload);
      setForm(EMPTY);
    } catch (err) {
      setError(err?.message || 'Création impossible');
    }
  };

  return (
    <Modal isOpen={open} onClose={handleClose} title={title} maxWidth="max-w-lg">
      <form onSubmit={handleSubmit} className="space-y-3">
        <SettingsTextField label="Commerce / société *" value={form.businessName} onChange={setField('businessName')} />
        <SettingsTextField label="Nom du contact *" value={form.name} onChange={setField('name')} />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <SettingsTextField label="Téléphone" value={form.phone} onChange={setField('phone')} />
          <SettingsTextField label="Email" type="email" value={form.email} onChange={setField('email')} />
        </div>
        <SettingsTextField label="Ville" value={form.city} onChange={setField('city')} />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1.5 block text-[13px] font-medium text-gray-600">Source</span>
            <select
              value={form.utmSource}
              onChange={(e) => setField('utmSource')(e.target.value)}
              className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
            >
              {SOURCE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="mb-1.5 block text-[13px] font-medium text-gray-600">Statut</span>
            <select
              value={form.status}
              onChange={(e) => setField('status')(e.target.value)}
              className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
            >
              {Object.entries(LEAD_STATUS_LABEL).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </label>
        </div>
        <SettingsTextField
          label="Prochaine relance"
          type="datetime-local"
          value={form.nextFollowUpAt}
          onChange={setField('nextFollowUpAt')}
        />
        <label className="block">
          <span className="mb-1.5 block text-[13px] font-medium text-gray-600">Note</span>
          <textarea
            value={form.internalNotes}
            onChange={(e) => setField('internalNotes')(e.target.value)}
            rows={3}
            className="w-full rounded-xl border border-gray-200 px-3.5 py-2.5 text-sm"
          />
        </label>
        {error ? <p className="text-sm text-red-600">{error}</p> : null}
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={handleClose}
            className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={busy}
            className="rounded-xl bg-primary-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {busy ? 'Création…' : submitLabel}
          </button>
        </div>
      </form>
    </Modal>
  );
}
